import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Bot, 
  FileText, 
  HelpCircle, 
  X, 
  Send,
  Upload,
  Copy,
  CheckCircle,
  AlertCircle,
  Loader2,
  ChevronDown,
  ChevronRight,
  Image as ImageIcon,
  Terminal,
  ExternalLink,
  ArrowRight,
  Sparkles,
  RefreshCw
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { apiClient } from '@/lib/api';
import { toast } from 'sonner';
import { PillButton } from '@/components/ios26/IOSComponents';
import { 
  routeToPlaybook, 
  getConnectPlaybook, 
  CTA_ACTIONS,
  INTENT_TYPES 
} from '@/lib/helpPlaybooks';
import { PLATFORMS_CONFIG, getPlatformName } from '@/lib/platformConstants';

/**
 * DiagnosticsPanel - iOS 26-Inspired
 * ===================================
 * Persistent right-side panel with:
 * - Assistant technique (chat)
 * - Diagnostic (logs/curl/checks)
 * - Support (ticket)
 * 
 * USES SEMANTIC TOKENS for dark mode compatibility
 */
export default function DiagnosticsPanel({ onClose, projectId }) {
  const [activeTab, setActiveTab] = useState('assistant');
  
  const tabs = [
    { id: 'assistant', label: 'Assistant', icon: Bot },
    { id: 'diagnostic', label: 'Diagnostic', icon: FileText },
    { id: 'support', label: 'Support', icon: HelpCircle }
  ];
  
  return (
    <div className="fixed top-0 right-0 z-40 h-full w-full sm:w-96 bg-semantic-surface backdrop-blur-xl border-l border-semantic flex flex-col shadow-2xl ios-animate-slide-in">
      {/* Header */}
      <div className="p-4 border-b border-semantic-subtle flex items-center justify-between bg-semantic-surface">
        <h3 className="font-semibold text-semantic">Aide & Diagnostic</h3>
        <button 
          onClick={onClose}
          className="p-2 hover:bg-semantic-surface-2 rounded-xl transition-colors"
          data-testid="close-diagnostics-panel"
        >
          <X className="w-5 h-5 text-semantic-muted" />
        </button>
      </div>
      
      {/* Tabs */}
      <div className="flex border-b border-semantic-subtle p-1.5 gap-1 bg-semantic-surface-2">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            data-testid={`diagnostics-tab-${tab.id}`}
            className={cn(
              'flex-1 flex items-center justify-center gap-1.5 py-2.5 px-3 rounded-xl text-sm font-medium transition-all',
              activeTab === tab.id 
                ? 'bg-semantic-primary text-white shadow-md' 
                : 'text-semantic-muted hover:bg-semantic-surface'
            )}
          >
            <tab.icon className="w-4 h-4" />
            <span className="hidden sm:inline">{tab.label}</span>
          </button>
        ))}
      </div>
      
      {/* Content */}
      <div className="flex-1 overflow-hidden">
        {activeTab === 'assistant' && (
          <AssistantTab projectId={projectId} />
        )}
        {activeTab === 'diagnostic' && (
          <DiagnosticTab projectId={projectId} />
        )}
        {activeTab === 'support' && (
          <SupportTab projectId={projectId} />
        )}
      </div>
    </div>
  );
}

/**
 * Assistant Tab - AI Chat with Contextual Guidance + Playbooks
 * ============================================================
 * P0: Assistant contextuel généralisé pour tous les canaux
 * 
 * Features:
 * - Playbooks par plateforme et intention
 * - CTAs actionnables (open_platform_modal, go_to_step, etc.)
 * - Routeur d'intention basé sur contexte
 * - Messages contextuels proactifs
 */
function AssistantTab({ projectId }) {
  const navigate = useNavigate();
  const location = useLocation();
  
  // State
  const [messages, setMessages] = useState([]);
  const [projectStatus, setProjectStatus] = useState(null);
  const [inputValue, setInputValue] = useState('');
  const [logsInput, setLogsInput] = useState('');
  const [showLogsInput, setShowLogsInput] = useState(false);
  const [sending, setSending] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [activePlaybook, setActivePlaybook] = useState(null);
  const [lastUserAction, setLastUserAction] = useState(null);
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  // Derive current step from URL
  const currentStep = useCallback(() => {
    const path = location.pathname;
    if (path.includes('/analyze')) return 'analyze';
    if (path.includes('/connect')) return 'connect';
    if (path.includes('/campaign')) return 'campaign';
    if (path.includes('/generate')) return 'generate';
    if (path.includes('/results')) return 'results';
    return null;
  }, [location.pathname]);

  // Build context payload for playbook routing
  const buildContextPayload = useCallback(() => {
    return {
      currentStep: currentStep(),
      selectedPlatform,
      channelsStatus: projectStatus?.channels || {},
      lastUserAction,
      lastError: null, // Could be tracked from error events
      userMessage: inputValue,
      projectStatus
    };
  }, [currentStep, selectedPlatform, projectStatus, lastUserAction, inputValue]);

  // CTA Action Handler
  const handleCtaAction = useCallback((cta) => {
    const { action, payload } = cta;
    
    switch (action) {
      case CTA_ACTIONS.OPEN_PLATFORM_MODAL:
        // Dispatch event to open platform modal
        window.dispatchEvent(new CustomEvent('ame:open-platform-modal', {
          detail: { platform: payload?.platform }
        }));
        setSelectedPlatform(payload?.platform);
        setLastUserAction({ type: 'CLICK_CONFIGURE_PLATFORM', payload });
        toast.info(`Configuration de ${getPlatformName(payload?.platform)}...`);
        break;
        
      case CTA_ACTIONS.GO_TO_STEP:
        const stepRoutes = {
          analyze: `/projects/${projectId}/setup/analyze`,
          connect: `/projects/${projectId}/setup/connect`,
          campaign: `/projects/${projectId}/setup/campaign`,
          generate: `/projects/${projectId}/setup/generate`,
          results: `/projects/${projectId}/setup/results`
        };
        if (stepRoutes[payload?.step]) {
          navigate(stepRoutes[payload.step]);
        }
        break;
        
      case CTA_ACTIONS.RETRY_CONNECTION:
        window.dispatchEvent(new CustomEvent('ame:retry-connection', {
          detail: { platform: payload?.platform }
        }));
        break;
        
      case CTA_ACTIONS.OPEN_EXTERNAL_LINK:
        if (payload?.url) {
          window.open(payload.url, '_blank', 'noopener,noreferrer');
        }
        break;
        
      case CTA_ACTIONS.CONTACT_SUPPORT:
        // Switch to support tab
        window.dispatchEvent(new CustomEvent('ame:switch-diagnostics-tab', {
          detail: { tab: 'support' }
        }));
        break;
        
      case CTA_ACTIONS.DISMISS:
        setActivePlaybook(null);
        break;
        
      // P0 UX OVERHAUL: Copy to clipboard action
      case CTA_ACTIONS.COPY_TO_CLIPBOARD:
        if (payload?.text) {
          const fullUrl = payload.text.startsWith('/') 
            ? `${window.location.origin}${payload.text}` 
            : payload.text;
          navigator.clipboard.writeText(fullUrl)
            .then(() => toast.success(`${payload.label || 'Texte'} copié !`))
            .catch(() => toast.error('Erreur lors de la copie'));
        }
        break;
        
      default:
        console.warn('Unknown CTA action:', action);
    }
  }, [navigate, projectId]);

  // Load project status and set initial playbook
  useEffect(() => {
    const loadContext = async () => {
      try {
        if (projectId) {
          const response = await apiClient.get(`/projects/${projectId}/status`);
          setProjectStatus(response.data);
          
          // Route to appropriate playbook based on context
          const context = {
            currentStep: currentStep(),
            channelsStatus: response.data?.channels || {},
            projectStatus: response.data
          };
          const playbook = routeToPlaybook(context);
          
          if (playbook) {
            setActivePlaybook(playbook);
          }
          
          // Generate welcome message
          const welcomeMsg = getContextualWelcome(response.data);
          setMessages([{ type: 'ai', content: welcomeMsg, hasPlaybook: !!playbook }]);
        } else {
          setMessages([{
            type: 'ai',
            content: `👋 Bonjour ! Je suis votre assistant.\n\nJe peux vous aider à :\n• Créer et configurer votre projet\n• Connecter vos réseaux sociaux\n• Comprendre les erreurs\n\n**Commencez par créer un projet** ou posez-moi une question.`
          }]);
        }
      } catch (error) {
        console.error('Failed to load project status:', error);
        setMessages([{
          type: 'ai',
          content: `👋 Bonjour ! Je suis votre assistant technique.\n\nJe peux vous aider avec vos questions sur la configuration et l'utilisation de la plateforme.`
        }]);
      }
    };
    
    loadContext();
  }, [projectId, currentStep]);

  // Re-route playbook when step changes
  useEffect(() => {
    if (projectStatus) {
      const context = buildContextPayload();
      const playbook = routeToPlaybook(context);
      setActivePlaybook(playbook);
    }
  }, [location.pathname, projectStatus, buildContextPayload]);

  // Listen for assistant events from other components
  useEffect(() => {
    const handleAssistantEvent = (event) => {
      const { type, payload } = event.detail || {};
      setLastUserAction({ type, payload });
      
      if (type === 'CLICK_CONFIGURE_PLATFORM' && payload?.platform) {
        const playbook = getConnectPlaybook(payload.platform);
        if (playbook) {
          setActivePlaybook(playbook);
          setSelectedPlatform(payload.platform);
        }
      }
    };

    window.addEventListener('ame:assistant-event', handleAssistantEvent);
    return () => window.removeEventListener('ame:assistant-event', handleAssistantEvent);
  }, []);
  
  // Generate contextual welcome message
  const getContextualWelcome = (status) => {
    const steps = status?.steps || {};
    const channels = status?.channels || {};
    const connectedCount = Object.values(channels).filter(c => c?.connected).length;
    const connectedNames = Object.entries(channels)
      .filter(([_, c]) => c?.connected)
      .map(([id]) => getPlatformName(id))
      .join(', ');
    
    if (!steps.website_analyzed) {
      return `👋 Bienvenue ! Votre projet est prêt.\n\n**Prochaine étape : Analyser votre site**\nEntrez l'URL de votre site web et cliquez sur "Analyser" pour extraire vos thèmes marketing.`;
    }
    
    if (!steps.themes_validated) {
      return `📊 Analyse terminée !\n\n**Prochaine étape : Valider les thèmes**\nVérifiez les thèmes extraits et validez-les pour continuer.`;
    }
    
    if (connectedCount === 0) {
      return `✅ Thèmes validés !\n\n**Prochaine étape : Connecter vos comptes**\nCliquez sur une plateforme ci-dessous pour commencer.`;
    }
    
    if (!steps.campaign_configured) {
      return `🔗 ${connectedCount} plateforme(s) connectée(s) : ${connectedNames}\n\n**Prochaine étape : Créer votre campagne**\nDéfinissez votre objectif et fréquence de publication.`;
    }
    
    if (!steps.content_generated) {
      return `🎯 Campagne configurée !\n\n**Prochaine étape : Générer 30 jours de contenu**\nCliquez sur "Générer" pour créer vos publications.`;
    }
    
    return `🎉 Félicitations ! Votre campagne est prête.\n\n✅ Site analysé et thèmes validés\n✅ ${connectedCount} plateforme(s) connectée(s)\n✅ Campagne et contenu générés\n\nConsultez vos **Résultats** pour voir vos publications.`;
  };
  
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };
  
  useEffect(() => {
    scrollToBottom();
  }, [messages, activePlaybook]);
  
  const handleSendMessage = async (messageContent = inputValue) => {
    if (!messageContent.trim() || sending) return;
    
    const userMessage = messageContent.trim();
    setInputValue('');
    setLogsInput('');
    setShowLogsInput(false);
    setMessages(prev => [...prev, { type: 'user', content: userMessage }]);
    setSending(true);

    // Check if message matches a platform - show playbook
    const context = { ...buildContextPayload(), userMessage };
    const playbook = routeToPlaybook(context);
    if (playbook) {
      setActivePlaybook(playbook);
    }
    
    try {
      const response = await apiClient.post('/ai/tech-assistant', {
        message: userMessage,
        context: 'setup',
        project_id: projectId,
        project_status: projectStatus
      });
      
      setMessages(prev => [...prev, { 
        type: 'ai', 
        content: response.data.response,
        diagnosis: response.data.diagnosis,
        wasRedacted: response.data.was_redacted
      }]);
    } catch (error) {
      setMessages(prev => [...prev, { 
        type: 'ai', 
        content: `❌ Désolé, je n'ai pas pu traiter votre demande.\n\n**Que faire ?**\n1. Vérifiez votre connexion internet\n2. Réessayez dans quelques instants\n3. Si le problème persiste, ouvrez un ticket Support`
      }]);
    } finally {
      setSending(false);
    }
  };
  
  const handleLogsSubmit = () => {
    if (logsInput.trim()) {
      handleSendMessage(`Voici les logs/erreur:\n\`\`\`\n${logsInput}\n\`\`\``);
    }
  };
  
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploadingImage(true);
    setMessages(prev => [...prev, { 
      type: 'user', 
      content: `📷 Image uploadée: ${file.name}`,
      isImage: true
    }]);
    
    setMessages(prev => [...prev, { 
      type: 'ai', 
      content: `J'ai bien reçu votre capture d'écran.\n\nPour mieux vous aider, pouvez-vous me décrire :\n1. Quelle action vous effectuiez ?\n2. Quel message d'erreur s'affiche ?\n3. Sur quelle plateforme ?`
    }]);
    
    setUploadingImage(false);
  };

  // Refresh status
  const handleRefreshStatus = async () => {
    if (!projectId) return;
    try {
      const response = await apiClient.get(`/projects/${projectId}/status`);
      setProjectStatus(response.data);
      toast.success('Statut actualisé');
    } catch (error) {
      toast.error('Erreur lors de l\'actualisation');
    }
  };
  
  return (
    <div className="h-full flex flex-col bg-semantic">
      {/* Active Playbook Card */}
      {activePlaybook && (
        <PlaybookCard 
          playbook={activePlaybook} 
          onCtaClick={handleCtaAction}
          onDismiss={() => setActivePlaybook(null)}
          channelsStatus={projectStatus?.channels || {}}
        />
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((msg, i) => (
          <div
            key={i}
            className={cn(
              'max-w-[90%] rounded-2xl text-sm',
              msg.type === 'user' 
                ? 'ml-auto p-3 rounded-br-md' 
                : 'p-4 rounded-bl-md'
            )}
            style={{
              backgroundColor: msg.type === 'user' 
                ? 'hsl(var(--chat-bubble-user))' 
                : 'hsl(var(--chat-bubble-agent))',
              color: msg.type === 'user' 
                ? 'hsl(var(--chat-bubble-user-text))' 
                : 'hsl(var(--chat-bubble-agent-text))'
            }}
          >
            {msg.wasRedacted && (
              <div className="flex items-center gap-2 mb-2 p-2 rounded-lg text-xs chip-semantic-warning">
                <AlertCircle className="w-4 h-4" />
                Données sensibles masquées
              </div>
            )}
            <div 
              className="whitespace-pre-wrap prose prose-sm max-w-none"
              dangerouslySetInnerHTML={{ 
                __html: msg.content
                  .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                  .replace(/\n/g, '<br/>')
                  .replace(/```([\s\S]*?)```/g, '<pre style="background: hsl(var(--chat-code-bg)); color: hsl(var(--chat-code-text)); padding: 0.5rem; border-radius: 0.5rem; margin-top: 0.5rem; font-size: 0.75rem; overflow-x: auto;">$1</pre>')
              }}
            />
            {msg.diagnosis?.matched && (
              <div className="mt-2 pt-2 border-t text-xs text-semantic-muted" style={{ borderColor: 'hsl(var(--color-border-subtle))' }}>
                <CheckCircle className="w-3 h-3 inline mr-1 text-semantic-success" />
                Problème reconnu: {msg.diagnosis.issue_id}
              </div>
            )}
          </div>
        ))}
        {sending && (
          <div 
            className="rounded-2xl rounded-bl-md p-4 max-w-[85%]"
            style={{
              backgroundColor: 'hsl(var(--chat-bubble-agent))',
              color: 'hsl(var(--chat-bubble-agent-text))'
            }}
          >
            <div className="flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin text-semantic-primary" />
              <span className="text-sm text-semantic-muted">Analyse en cours...</span>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>
      
      {/* Logs/Curl Input Toggle */}
      {showLogsInput && (
        <div className="px-4 pb-2">
          <div className="rounded-xl p-3 border border-semantic bg-semantic-surface-2">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-semantic-muted">Coller vos logs / curl / erreur</span>
              <button 
                onClick={() => setShowLogsInput(false)}
                className="p-1 hover:bg-semantic-surface-3 rounded"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
            <textarea
              value={logsInput}
              onChange={(e) => setLogsInput(e.target.value)}
              placeholder="Collez ici le message d'erreur, la sortie curl, ou les logs..."
              className="w-full h-24 px-3 py-2 border rounded-lg text-xs font-mono resize-none focus:outline-none focus:ring-2 input-semantic"
              style={{
                backgroundColor: 'hsl(var(--input-bg))',
                borderColor: 'hsl(var(--input-border))',
                color: 'hsl(var(--input-text))'
              }}
            />
            <div className="flex justify-end mt-2">
              <PillButton 
                variant="primary" 
                size="sm"
                onClick={handleLogsSubmit}
                disabled={!logsInput.trim()}
              >
                <Terminal className="w-3 h-3" />
                Analyser
              </PillButton>
            </div>
          </div>
        </div>
      )}
      
      {/* Input Area */}
      <div className="p-4 border-t border-semantic bg-semantic-surface">
        <div className="flex gap-2 mb-2">
          <button
            onClick={() => setShowLogsInput(!showLogsInput)}
            className={cn(
              'flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all',
              showLogsInput 
                ? 'bg-semantic-primary text-white' 
                : 'bg-semantic-surface-2 text-semantic-2 hover:bg-semantic-surface-3'
            )}
            data-testid="logs-toggle-btn"
          >
            <Terminal className="w-3 h-3" />
            Logs / Curl
          </button>
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploadingImage}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-semantic-surface-2 text-semantic-2 hover:bg-semantic-surface-3 transition-all"
            data-testid="capture-btn"
          >
            <ImageIcon className="w-3 h-3" />
            Capture
          </button>
          <button
            onClick={handleRefreshStatus}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-semantic-surface-2 text-semantic-2 hover:bg-semantic-surface-3 transition-all"
            data-testid="refresh-status-btn"
          >
            <RefreshCw className="w-3 h-3" />
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleImageUpload}
            className="hidden"
          />
        </div>
        
        <div className="flex gap-2">
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSendMessage()}
            placeholder="Décrivez votre problème ou posez une question..."
            className="flex-1 px-4 py-2.5 border-0 rounded-full text-sm focus:outline-none focus:ring-2"
            style={{
              backgroundColor: 'hsl(var(--chat-input-bg))',
              color: 'hsl(var(--chat-input-text))',
              boxShadow: '0 0 0 1px hsl(var(--chat-input-border))'
            }}
            data-testid="assistant-input"
          />
          <button
            onClick={() => handleSendMessage()}
            disabled={!inputValue.trim() || sending}
            className="p-2.5 rounded-full hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            style={{
              backgroundColor: 'hsl(var(--color-primary))',
              color: 'hsl(var(--color-primary-text))'
            }}
            data-testid="send-message-btn"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * PlaybookCard - Renders a contextual help playbook with CTAs
 * Displays title, steps, tips, and actionable buttons
 * P0 UX OVERHAUL: Now includes technical data (Callback URL, Scopes, etc.)
 */
function PlaybookCard({ playbook, onCtaClick, onDismiss, channelsStatus }) {
  const [expanded, setExpanded] = useState(true);
  const [showTechnical, setShowTechnical] = useState(false);
  
  if (!playbook) return null;

  // Get platform connection status for this playbook
  const platformStatus = playbook.platform 
    ? channelsStatus[playbook.platform] 
    : null;
  const isConnected = platformStatus?.connected;
  
  // Check if playbook has technical data
  const hasTechnicalData = playbook.technical || playbook.commonErrors;

  return (
    <div 
      className="mx-4 mt-4 rounded-2xl overflow-hidden border"
      style={{ 
        backgroundColor: 'hsl(var(--color-surface-2))',
        borderColor: 'hsl(var(--color-border))'
      }}
      data-testid={`playbook-card-${playbook.id}`}
    >
      {/* Header */}
      <div 
        className="p-4 flex items-center justify-between cursor-pointer"
        onClick={() => setExpanded(!expanded)}
        style={{ borderBottom: expanded ? '1px solid hsl(var(--color-border-subtle))' : 'none' }}
      >
        <div className="flex items-center gap-3">
          <div 
            className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{ 
              background: playbook.platform 
                ? PLATFORMS_CONFIG[playbook.platform]?.gradient || 'hsl(var(--color-primary))'
                : 'hsl(var(--color-primary))'
            }}
          >
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div>
            <h4 className="font-semibold text-sm text-semantic">{playbook.title}</h4>
            <p className="text-xs text-semantic-muted">{playbook.summary}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isConnected && (
            <span className="flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full chip-semantic-success">
              <CheckCircle className="w-3 h-3" />
              Connecté
            </span>
          )}
          <button
            onClick={(e) => { e.stopPropagation(); onDismiss(); }}
            className="p-1.5 rounded-lg hover:bg-semantic-surface-3 transition-colors"
            data-testid="playbook-dismiss-btn"
          >
            <X className="w-4 h-4 text-semantic-muted" />
          </button>
          <ChevronDown 
            className={cn(
              'w-4 h-4 text-semantic-muted transition-transform',
              expanded ? '' : '-rotate-90'
            )} 
          />
        </div>
      </div>

      {/* Expanded Content */}
      {expanded && (
        <div className="p-4 space-y-4">
          {/* Steps */}
          {playbook.steps && playbook.steps.length > 0 && (
            <div className="space-y-3">
              {playbook.steps.map((step, i) => (
                <div key={i} className="flex gap-3">
                  <div 
                    className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                    style={{ 
                      backgroundColor: 'hsl(var(--color-primary) / 0.1)',
                      color: 'hsl(var(--color-primary))'
                    }}
                  >
                    {step.order || i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-semantic">{step.title}</p>
                    <p className="text-xs text-semantic-muted mt-0.5">{step.description}</p>
                    {step.tip && (
                      <p className="text-xs text-semantic-2 mt-1 italic">💡 {step.tip}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* P0 UX OVERHAUL: Technical Details Toggle */}
          {hasTechnicalData && (
            <button
              onClick={() => setShowTechnical(!showTechnical)}
              className="w-full flex items-center justify-between py-2 px-3 rounded-lg text-sm transition-colors"
              style={{ backgroundColor: 'hsl(var(--color-surface-3))' }}
              data-testid="toggle-technical-details"
            >
              <span className="flex items-center gap-2 text-semantic-2">
                <Terminal className="w-4 h-4" />
                Détails techniques
              </span>
              <ChevronDown className={cn('w-4 h-4 text-semantic-muted transition-transform', showTechnical && 'rotate-180')} />
            </button>
          )}
          
          {/* P0 UX OVERHAUL: Technical Section (collapsible) */}
          {showTechnical && playbook.technical && (
            <div className="space-y-3 p-3 rounded-xl" style={{ backgroundColor: 'hsl(var(--color-surface))' }}>
              {/* Callback URL */}
              {playbook.technical.callbackUrl && (
                <div>
                  <label className="block text-xs font-medium text-semantic-muted mb-1">Callback URL</label>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 px-2 py-1.5 rounded text-xs font-mono bg-semantic-surface-2 text-semantic-2 truncate">
                      {`${window.location.origin}${playbook.technical.callbackUrl}`}
                    </code>
                    <button
                      onClick={() => onCtaClick({ 
                        action: CTA_ACTIONS.COPY_TO_CLIPBOARD, 
                        payload: { text: playbook.technical.callbackUrl, label: 'Callback URL' }
                      })}
                      className="p-1.5 rounded-lg hover:bg-semantic-surface-3 transition-colors"
                      data-testid="copy-callback-inline"
                    >
                      <Copy className="w-3.5 h-3.5 text-semantic-muted" />
                    </button>
                  </div>
                </div>
              )}
              
              {/* Scopes */}
              {playbook.technical.scopes && (
                <div>
                  <label className="block text-xs font-medium text-semantic-muted mb-1">Permissions (Scopes)</label>
                  <div className="flex flex-wrap gap-1.5">
                    {playbook.technical.scopes.map(scope => (
                      <span key={scope} className="px-2 py-0.5 rounded text-xs font-mono bg-semantic-surface-2 text-semantic-2">
                        {scope}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Prerequisites */}
              {playbook.technical.prerequisites && (
                <div>
                  <label className="block text-xs font-medium text-semantic-muted mb-1">Prérequis</label>
                  <ul className="space-y-1">
                    {playbook.technical.prerequisites.map((prereq, i) => (
                      <li key={i} className="text-xs text-semantic-2 flex items-start gap-1.5">
                        <span className="text-semantic-muted">•</span>
                        {prereq}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              {/* Links */}
              <div className="flex flex-wrap gap-2 pt-1">
                {playbook.technical.developerPortalUrl && (
                  <a
                    href={playbook.technical.developerPortalUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-lg bg-semantic-surface-2 hover:bg-semantic-surface-3 transition-colors"
                    style={{ color: 'hsl(var(--color-primary))' }}
                  >
                    <ExternalLink className="w-3 h-3" />
                    Portail développeur
                  </a>
                )}
                {playbook.technical.docUrl && (
                  <a
                    href={playbook.technical.docUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-lg bg-semantic-surface-2 hover:bg-semantic-surface-3 transition-colors"
                    style={{ color: 'hsl(var(--color-primary))' }}
                  >
                    <ExternalLink className="w-3 h-3" />
                    Documentation
                  </a>
                )}
              </div>
            </div>
          )}

          {/* Tips */}
          {playbook.tips && playbook.tips.length > 0 && (
            <div 
              className="p-3 rounded-xl"
              style={{ backgroundColor: 'hsl(var(--color-info-bg))' }}
            >
              <p className="text-xs font-medium text-semantic mb-2">💡 Conseils</p>
              <ul className="space-y-1">
                {playbook.tips.map((tip, i) => (
                  <li key={i} className="text-xs text-semantic-2">• {tip}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Troubleshooting / Common Errors */}
          {(playbook.troubleshooting?.length > 0 || playbook.commonErrors?.length > 0) && (
            <div className="space-y-2">
              <p className="text-xs font-medium text-semantic-muted">Problèmes fréquents</p>
              {playbook.troubleshooting?.map((item, i) => (
                <div key={`ts-${i}`} className="p-2 rounded-lg bg-semantic-surface">
                  <p className="text-xs font-medium text-semantic">{item.symptom}</p>
                  <p className="text-xs text-semantic-muted mt-0.5">→ {item.solution}</p>
                </div>
              ))}
              {playbook.commonErrors?.map((error, i) => (
                <div key={`err-${i}`} className="p-2 rounded-lg" style={{ backgroundColor: 'hsl(var(--color-warning-bg) / 0.5)' }}>
                  <p className="text-xs font-medium" style={{ color: 'hsl(var(--color-warning-text))' }}>{error.code}</p>
                  <p className="text-xs text-semantic-2 mt-0.5">{error.meaning}</p>
                  <p className="text-xs text-semantic-muted">→ {error.fix}</p>
                </div>
              ))}
            </div>
          )}

          {/* CTAs */}
          {playbook.ctas && playbook.ctas.length > 0 && (
            <div className="flex flex-wrap gap-2 pt-2">
              {playbook.ctas.map((cta, i) => (
                <button
                  key={i}
                  onClick={() => onCtaClick(cta)}
                  className={cn(
                    'flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all',
                    cta.variant === 'primary'
                      ? 'text-white shadow-md hover:opacity-90'
                      : 'text-semantic-2 hover:bg-semantic-surface-3'
                  )}
                  style={{
                    backgroundColor: cta.variant === 'primary' 
                      ? 'hsl(var(--color-primary))' 
                      : 'hsl(var(--color-surface-3))'
                  }}
                  data-testid={`playbook-cta-${i}`}
                >
                  {cta.action === CTA_ACTIONS.OPEN_EXTERNAL_LINK && (
                    <ExternalLink className="w-4 h-4" />
                  )}
                  {cta.action === CTA_ACTIONS.GO_TO_STEP && (
                    <ArrowRight className="w-4 h-4" />
                  )}
                  {cta.action === CTA_ACTIONS.OPEN_PLATFORM_MODAL && (
                    <ChevronRight className="w-4 h-4" />
                  )}
                  {cta.action === CTA_ACTIONS.COPY_TO_CLIPBOARD && (
                    <Copy className="w-4 h-4" />
                  )}
                  {cta.label}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Diagnostic Tab - View recent errors and copy bundle
 * USES SEMANTIC TOKENS for dark mode compatibility
 */
function DiagnosticTab({ projectId }) {
  const [diagnostics, setDiagnostics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copying, setCopying] = useState(false);
  const [autoShare, setAutoShare] = useState(false);
  
  useEffect(() => {
    loadDiagnostics();
  }, [projectId]);
  
  const loadDiagnostics = async () => {
    try {
      const response = await apiClient.get('/support/diagnostics/recent');
      setDiagnostics(response.data);
    } catch (error) {
      console.error('Failed to load diagnostics:', error);
      setDiagnostics({ recent_errors: [] });
    } finally {
      setLoading(false);
    }
  };
  
  const handleCopyDiagnostic = async () => {
    setCopying(true);
    
    const bundle = {
      timestamp: new Date().toISOString(),
      project_id: projectId,
      recent_errors: diagnostics?.recent_errors?.slice(0, 5) || [],
      browser: navigator.userAgent,
      // Tokens are NOT included - redacted by default
    };
    
    try {
      await navigator.clipboard.writeText(JSON.stringify(bundle, null, 2));
      toast.success('Diagnostic copié (données sensibles masquées)');
    } catch (error) {
      toast.error('Erreur lors de la copie');
    } finally {
      setCopying(false);
    }
  };
  
  if (loading) {
    return (
      <div className="p-6 text-center">
        <Loader2 className="w-8 h-8 animate-spin mx-auto" style={{ color: 'hsl(var(--color-primary))' }} />
        <p className="text-sm mt-2 text-semantic-muted">Chargement...</p>
      </div>
    );
  }
  
  return (
    <div className="p-4 space-y-4 overflow-y-auto h-full">
      <div className="text-sm text-semantic-muted mb-4">
        Dernières connexions et erreurs
      </div>
      
      {/* Auto-share toggle */}
      <div className="flex items-center justify-between p-3 rounded-xl bg-semantic-surface-2">
        <div className="flex items-center gap-2">
          <Upload className="w-4 h-4 text-semantic-muted" />
          <span className="text-sm text-semantic-2">Partager les logs automatiquement</span>
        </div>
        <button
          onClick={() => setAutoShare(!autoShare)}
          className={cn(
            'w-10 h-6 rounded-full transition-colors relative',
            autoShare ? 'bg-semantic-primary' : 'bg-semantic-surface-3'
          )}
          style={{
            backgroundColor: autoShare ? 'hsl(var(--color-primary))' : 'hsl(var(--color-surface-3))'
          }}
        >
          <div className={cn(
            'w-4 h-4 bg-white rounded-full absolute top-1 transition-transform shadow',
            autoShare ? 'translate-x-5' : 'translate-x-1'
          )} />
        </button>
      </div>
      
      {/* Error List */}
      {diagnostics?.recent_errors?.length > 0 ? (
        <div className="space-y-3">
          {diagnostics.recent_errors.slice(0, 5).map((error, i) => (
            <div key={i} className="p-3 border rounded-xl bg-semantic-surface" style={{ borderColor: 'hsl(var(--color-border))' }}>
              <div className="flex items-center justify-between mb-2">
                <span className={cn(
                  'text-xs font-medium px-2 py-0.5 rounded-full',
                  error.is_transient || error.transient_hint 
                    ? 'chip-semantic-warning' 
                    : 'chip-semantic-danger'
                )}>
                  {error.is_transient || error.transient_hint ? '⏳ transient' : '❌ erreur'}
                </span>
                <span className="text-xs text-semantic-muted">
                  {error.created_at ? new Date(error.created_at).toLocaleTimeString('fr') : '-'}
                </span>
              </div>
              <p className="text-sm font-medium text-semantic">{error.error_code || 'Unknown'}</p>
              <p className="text-xs text-semantic-muted truncate mt-1">{error.message || '-'}</p>
              {error.platform && (
                <span className="inline-block mt-2 text-xs px-2 py-0.5 rounded bg-semantic-surface-2 text-semantic-2">
                  {error.platform}
                </span>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-8">
          <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3 chip-semantic-success">
            <CheckCircle className="w-6 h-6" />
          </div>
          <p className="text-sm text-semantic-2">Aucune erreur récente</p>
          <p className="text-xs text-semantic-muted">Tout fonctionne correctement</p>
        </div>
      )}
      
      {/* Copy Button */}
      <PillButton 
        variant="secondary" 
        className="w-full"
        onClick={handleCopyDiagnostic}
        loading={copying}
        data-testid="copy-diagnostic-btn"
      >
        <Copy className="w-4 h-4" />
        Copier le diagnostic
      </PillButton>
      
      <p className="text-xs text-semantic-muted text-center">
        Les tokens et données sensibles sont automatiquement masqués
      </p>
    </div>
  );
}

/**
 * Support Tab - Create ticket
 * USES SEMANTIC TOKENS for dark mode compatibility
 */
function SupportTab({ projectId }) {
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [attachLogs, setAttachLogs] = useState(true);
  const [sending, setSending] = useState(false);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!subject.trim() || !message.trim()) {
      toast.error('Remplissez tous les champs');
      return;
    }
    
    setSending(true);
    try {
      await apiClient.post('/support/ticket', { 
        subject, 
        message,
        attach_logs: attachLogs,
        project_id: projectId
      });
      toast.success('Ticket envoyé ! Nous répondons sous 24h.');
      setSubject('');
      setMessage('');
    } catch (error) {
      toast.error('Erreur lors de l\'envoi');
    } finally {
      setSending(false);
    }
  };
  
  return (
    <div className="p-4 overflow-y-auto h-full">
      <div className="text-sm text-semantic-muted mb-4">
        Besoin d&apos;aide ? Ouvrez un ticket.
      </div>
      
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-semantic mb-1">
            Sujet
          </label>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Ex: Problème de connexion Instagram"
            className="w-full px-4 py-2.5 rounded-xl text-sm focus:outline-none focus:ring-2 input-semantic"
            data-testid="support-subject"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-semantic mb-1">
            Message
          </label>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Décrivez votre problème..."
            rows={4}
            className="w-full px-4 py-2.5 rounded-xl text-sm focus:outline-none focus:ring-2 resize-none input-semantic"
            data-testid="support-message"
          />
        </div>
        
        <div className="flex items-center gap-3 p-3 rounded-xl chip-semantic-info">
          <input 
            type="checkbox" 
            id="attach-logs" 
            checked={attachLogs}
            onChange={(e) => setAttachLogs(e.target.checked)}
            className="rounded"
            style={{ 
              borderColor: 'hsl(var(--color-border))',
              accentColor: 'hsl(var(--color-primary))'
            }}
          />
          <label htmlFor="attach-logs" className="text-sm">
            Joindre les logs techniques (recommandé)
          </label>
        </div>
        
        <PillButton 
          type="submit" 
          variant="primary" 
          className="w-full"
          loading={sending}
          data-testid="support-submit"
        >
          Envoyer le ticket
        </PillButton>
      </form>
      
      <div className="mt-6 p-4 rounded-xl bg-semantic-surface-2">
        <p className="text-sm font-medium text-semantic mb-2">📧 Contact direct</p>
        <a 
          href="mailto:support@ai-marketing-engine.com"
          className="text-sm hover:underline"
          style={{ color: 'hsl(var(--color-primary))' }}
        >
          support@ai-marketing-engine.com
        </a>
      </div>
    </div>
  );
}
